import type { GpuContext, TensorDType } from '@design-ai/gpu-core';
import { isDeviceShared, ort } from './runtime.js';

export interface OnnxModelOptions {
  /**
   * Keep outputs in GPU memory. Turning this off is only useful for debugging -
   * it forces every result through CPU memory.
   */
  outputsOnGpu?: boolean;
  /**
   * ORT graph capture. Big win for the tiled upscaler (the same shapes run
   * dozens of times) but it requires every shape to stay constant, so the
   * pipeline only enables it once tile size is pinned.
   */
  graphCapture?: boolean;
  freeDimensionOverrides?: Record<string, number>;
  /** Node names to keep on the CPU EP. */
  cpuNodes?: string[];
  label?: string;
}

/** A model output still living in GPU memory. */
export interface GpuTensor {
  buffer: GPUBuffer;
  dims: readonly number[];
  dtype: TensorDType;
  /**
   * Returns the buffer to ORT's allocator.
   *
   * Call only after the commands that read it have been submitted
   * (`ctx.flush()`), otherwise ORT may hand the same memory to the next
   * inference before our dispatches have run.
   */
  dispose(): void;
}

/**
 * Thin wrapper over `ort.InferenceSession` for the WebGPU execution provider.
 *
 * The value it adds is the GPU-resident calling convention: inputs arrive as a
 * `GPUBuffer` our compute shaders filled, outputs leave as a `GPUBuffer` the next
 * shader reads. Nothing in the hot path touches CPU memory.
 */
export class OnnxModel {
  private constructor(
    readonly session: ort.InferenceSession,
    private readonly outputsOnGpu: boolean,
    readonly label: string,
  ) {}

  static async create(model: ArrayBuffer, options: OnnxModelOptions = {}): Promise<OnnxModel> {
    const outputsOnGpu = options.outputsOnGpu !== false;
    const sessionOptions: ort.InferenceSession.SessionOptions = {
      executionProviders: [
        {
          name: 'webgpu',
          ...(options.cpuNodes ? { forceCpuNodeNames: options.cpuNodes } : {}),
        } as ort.InferenceSession.WebGpuExecutionProviderOption,
      ],
      graphOptimizationLevel: 'all',
      ...(outputsOnGpu ? { preferredOutputLocation: 'gpu-buffer' as const } : {}),
      ...(options.graphCapture ? { enableGraphCapture: true } : {}),
      ...(options.freeDimensionOverrides
        ? { freeDimensionOverrides: options.freeDimensionOverrides }
        : {}),
    };

    const session = await ort.InferenceSession.create(model, sessionOptions);
    return new OnnxModel(session, outputsOnGpu, options.label ?? 'model');
  }

  get inputNames(): readonly string[] {
    return this.session.inputNames;
  }

  get outputNames(): readonly string[] {
    return this.session.outputNames;
  }

  /**
   * Runs the model with a single GPU-resident input.
   *
   * `dims` must match the buffer's contents exactly; `packTensor` produces the
   * layout described by the model's `ModelIO`.
   */
  async runGpu(
    ctx: GpuContext,
    input: GPUBuffer,
    dims: readonly number[],
    dtype: TensorDType,
    outputIndex = 0,
  ): Promise<GpuTensor> {
    const inputName = this.session.inputNames[0];
    if (!inputName) throw new Error(`[inference] ${this.label} declares no inputs`);

    // Buffers belong to the device that created them. When ORT ended up on a
    // device of its own, the only correct handoff is through CPU memory.
    const inputTensor = isDeviceShared()
      ? ort.Tensor.fromGpuBuffer(input as never, { dataType: dtype, dims: [...dims] })
      : await stagedInputTensor(ctx, input, dims, dtype);

    let outputs: ort.InferenceSession.OnnxValueMapType;
    try {
      outputs = await this.session.run({ [inputName]: inputTensor });
    } finally {
      // We own the input buffer; drop only ORT's wrapper around it.
      inputTensor.dispose();
    }

    const outputName = this.session.outputNames[outputIndex];
    if (!outputName) {
      throw new Error(`[inference] ${this.label} has no output at index ${outputIndex}`);
    }
    const tensor = outputs[outputName] as ort.Tensor | undefined;
    if (!tensor) throw new Error(`[inference] ${this.label} returned no "${outputName}"`);

    // Every other output is dead weight; release it now.
    for (const [name, value] of Object.entries(outputs)) {
      if (name !== outputName) (value as ort.Tensor).dispose?.();
    }

    return this.#toGpuTensor(ctx, tensor);
  }

  #toGpuTensor(ctx: GpuContext, tensor: ort.Tensor): GpuTensor {
    const dtype = tensor.type === 'float16' ? 'float16' : 'float32';

    if (this.outputsOnGpu && tensor.location === 'gpu-buffer') {
      return {
        buffer: tensor.gpuBuffer as unknown as GPUBuffer,
        dims: tensor.dims,
        dtype,
        dispose: () => tensor.dispose(),
      };
    }

    // Fallback: ORT decided to keep this output on the CPU (or GPU outputs were
    // disabled). Upload it so the rest of the pipeline is unaffected.
    const data = tensor.data as Float32Array | Uint16Array;
    // ORT's wasm heap can be a SharedArrayBuffer when threading is on, which
    // `writeBuffer` will not accept - copy into a plain buffer first.
    const bytes = new Uint8Array(data.byteLength);
    bytes.set(new Uint8Array(data.buffer as ArrayBufferLike, data.byteOffset, data.byteLength));
    const buffer = ctx.device.createBuffer({
      label: `${this.label}-output-upload`,
      size: Math.ceil(bytes.byteLength / 4) * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
    });
    ctx.queue.writeBuffer(buffer, 0, bytes);
    tensor.dispose();
    return {
      buffer,
      dims: tensor.dims,
      dtype,
      dispose: () => buffer.destroy(),
    };
  }

  async dispose(): Promise<void> {
    await this.session.release();
  }
}

/**
 * Fallback path: read a GPU buffer back and wrap it as a CPU tensor.
 *
 * Only reached when ORT is on a different device. It keeps the pipeline correct
 * at a real cost, which is much better than an opaque validation error.
 */
async function stagedInputTensor(
  ctx: GpuContext,
  buffer: GPUBuffer,
  dims: readonly number[],
  dtype: TensorDType,
): Promise<ort.Tensor> {
  const byteLength = buffer.size;
  const staging = ctx.device.createBuffer({
    label: 'tensor-download',
    size: byteLength,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  });
  ctx.encoder().copyBufferToBuffer(buffer, 0, staging, 0, byteLength);
  ctx.flush();

  try {
    await staging.mapAsync(GPUMapMode.READ);
    const copy = staging.getMappedRange().slice(0);
    staging.unmap();
    const elements = dims.reduce((product, dim) => product * dim, 1);
    const data =
      dtype === 'float16'
        ? new Uint16Array(copy, 0, elements)
        : new Float32Array(copy, 0, elements);
    return new ort.Tensor(dtype, data as never, [...dims]);
  } finally {
    staging.destroy();
  }
}

/**
 * Exact-size `GPUBuffer` cache for model inputs.
 *
 * ORT validates that a wrapped buffer matches the tensor's byte length, so these
 * cannot come from the general pool (which rounds up to a power of two). Sizes
 * repeat constantly - one per model input shape - so an exact-size map hits
 * essentially always.
 */
export class TensorBufferCache {
  readonly #buffers = new Map<number, GPUBuffer>();

  constructor(private readonly ctx: GpuContext) {}

  get(byteLength: number, label = 'tensor'): GPUBuffer {
    const size = Math.ceil(byteLength / 4) * 4;
    let buffer = this.#buffers.get(size);
    if (!buffer) {
      buffer = this.ctx.device.createBuffer({
        label: `${label}-${size}`,
        size,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      });
      this.#buffers.set(size, buffer);
    }
    return buffer;
  }

  destroy(): void {
    for (const buffer of this.#buffers.values()) buffer.destroy();
    this.#buffers.clear();
  }
}
